import { apiClient } from '@/services/apiClient';
import type { Vendor, VendorContact, PaginatedResponse } from '@/types';

export interface VendorListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  riskTier?: string;
  departmentScope?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateVendorInput {
  vendorName: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  vendorCategory?: string;
  departmentScope: string;
  riskTier: string;
  dataHandlingClass?: string;
}

export type UpdateVendorInput = Partial<CreateVendorInput> & { updatedAt: string };

export interface CreateContactInput {
  contactName: string;
  email?: string;
  phone?: string;
  contactRole: string;
  notes?: string;
  isPrimary?: boolean;
}

export type UpdateContactInput = Partial<CreateContactInput> & { updatedAt?: string };

export const vendorService = {
  list(params?: VendorListParams): Promise<PaginatedResponse<Vendor>> {
    return apiClient.get('vendors', params as Record<string, string | number | boolean | undefined>);
  },

  getById(id: number): Promise<Vendor> {
    return apiClient.get(`vendors/${id}`);
  },

  create(input: CreateVendorInput): Promise<Vendor> {
    return apiClient.post('vendors', input);
  },

  update(id: number, input: UpdateVendorInput): Promise<Vendor> {
    return apiClient.put(`vendors/${id}`, input);
  },

  delete(id: number): Promise<void> {
    return apiClient.delete(`vendors/${id}`);
  },

  // Contacts
  listContacts(vendorId: number): Promise<VendorContact[]> {
    return apiClient.get(`vendors/${vendorId}/contacts`);
  },

  createContact(vendorId: number, input: CreateContactInput): Promise<VendorContact> {
    return apiClient.post(`vendors/${vendorId}/contacts`, input);
  },

  updateContact(vendorId: number, contactId: number, input: UpdateContactInput): Promise<VendorContact> {
    return apiClient.put(`vendors/${vendorId}/contacts/${contactId}`, input);
  },

  deleteContact(vendorId: number, contactId: number): Promise<void> {
    return apiClient.delete(`vendors/${vendorId}/contacts/${contactId}`);
  },

  // OSINT
  runOSINTScan(vendorId: number): Promise<{ message: string; totalSignals: number; alertsCreated: number; scanners: Array<{ source: string; success: boolean; signals: number; error?: string }> }> {
    return apiClient.post(`vendors/${vendorId}/osint-scan`);
  },
};
